"use client";

import { CMSData } from "@/cms/models";
import { ApplicationFraction } from "@/utils/gql/graphql";
import React from "react";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { getDesktop, getMobile } from "@/utils/theme-helper";

export interface SliderComponentProps {
  cmsData: CMSData;
  app: ApplicationFraction;
  items: any[];
  config: {
    navigation?: boolean;
    reserveLayout?: boolean;
    pagination?: {
      enabled: boolean;
      bulletColor?: string;
      bulletActiveColor?: string;
    };
    autoplay?: {
      delay: number;
      disableOnInteraction: boolean;
      pauseOnMouseEnter: boolean;
    };
    slidesPerView?: number | "auto" | string;
    spaceBetween?: string;
    centeredSlides?: boolean;
    speed?: number;
    effect?: string;
  };
}

const SliderClientComponent: React.FC<{
  config: SliderComponentProps;
  items: any[];
}> = ({ config, items }) => {
  if (!items || items.length === 0) {
    return null;
  }

  const slidesPerViewMobileString = getMobile(
    "1",
    (config.config.slidesPerView ?? "1").toString(),
  );
  let slidesPerViewMobile: number | "1" = parseInt(slidesPerViewMobileString);
  if (isNaN(slidesPerViewMobile)) {
    slidesPerViewMobile = 1;
  }

  const slidesPerViewDesktopString = getDesktop(
    "1",
    (config.config.slidesPerView ?? "1").toString(),
  );
  let slidesPerViewDesktop: number | "1" = parseInt(slidesPerViewDesktopString);
  if (isNaN(slidesPerViewDesktop)) {
    slidesPerViewDesktop = 1;
  }

  return (
    <div
      className={`relative h-full overflow-hidden ${config.config.reserveLayout ? "re-slider-reserved" : ""}`}
      style={
        {
          "--re-slider-count-mobile": Math.max(1, slidesPerViewMobile),
          "--re-slider-count-desktop": Math.max(1, slidesPerViewDesktop),
          "--re-slider-gap": `${Math.max(0, parseFloat(config.config.spaceBetween ?? "1") || 0)}px`,
          "--swiper-pagination-bullet-inactive-opacity":
            config.config.pagination?.bulletColor || 0.2,
          "--swiper-pagination-color":
            config.config.pagination?.bulletActiveColor ||
            "var(--button-bg-color)",
          "--swiper-pagination-bullet-inactive-color":
            config.config.pagination?.bulletColor || "var(--button-bg-color)",
        } as React.CSSProperties
      }
    >
      <Swiper
        className="select-none h-full"
        breakpoints={{
          768: {
            slidesPerView: slidesPerViewDesktop,
          },
        }}
        slidesPerView={slidesPerViewMobile}
        spaceBetween={config.config.spaceBetween ?? "1px"}
        centeredSlides={config.config.centeredSlides ?? false}
        speed={config.config.speed ?? 500}
        effect={config.config.effect ?? "slide"}
        fadeEffect={{
          crossFade: config.config.effect === "fade",
        }}
        navigation={config.config?.navigation}
        pagination={{
          enabled: config.config?.pagination?.enabled ?? false,
          clickable: true,
        }}
        autoplay={
          !config.config?.autoplay
            ? false
            : {
                delay: config.config.autoplay.delay ?? 1000,
                disableOnInteraction:
                  config.config.autoplay.disableOnInteraction ?? true,
                pauseOnMouseEnter:
                  config.config.autoplay.pauseOnMouseEnter ?? false,
              }
        }
        modules={
          [
            config.config.navigation ? Navigation : null,
            config.config.pagination ? Pagination : null,
            config.config.autoplay ? Autoplay : null,
            config.config.effect === "fade" ? EffectFade : null,
          ].filter(Boolean) as any
        }
      >
        {items.map((item, i) => {
          return (
            <SwiperSlide
              key={i}
              style={config.config.reserveLayout ? undefined : { width: "80%" }}
            >
              {item}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

export default SliderClientComponent;
